import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Route {
  id: string
  name: string
  type: string
  avg_transit_days: number
  cost_per_shipment: number
  on_time_rate: number
  shipments_mtd: number
  optimization_score: number
}

interface Carrier {
  id: string
  name: string
  performance_score: number
  cost_efficiency: number
  coverage: string
  shipments_mtd: number
  avg_cost: number
}

interface Statistics {
  total_shipments_mtd: number
  avg_transit_time: number
  on_time_delivery: number
  logistics_cost_mtd: number
  cost_per_order: number
  route_efficiency: number
}

export default function LogisticsOptimizationPage() {
  const [routes, setRoutes] = useState<Route[]>([])
  const [carriers, setCarriers] = useState<Carrier[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [routesRes, carriersRes, statsRes] = await Promise.all([
        fetch('/api/logisticsoptimization/routes'),
        fetch('/api/logisticsoptimization/carriers'),
        fetch('/api/logisticsoptimization/statistics')
      ])
      if (routesRes.ok) setRoutes(await routesRes.json())
      if (carriersRes.ok) setCarriers(await carriersRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching logistics optimization data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Logistics Optimization</h1>
          <p className="mt-2 text-gray-600">Phase 258 - Transportation and delivery efficiency</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Shipments MTD</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_shipments_mtd)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Transit</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_transit_time}d</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">On-Time</p>
              <p className="text-2xl font-bold text-green-600">{statistics.on_time_delivery}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Logistics Cost</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(statistics.logistics_cost_mtd)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Cost/Order</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(statistics.cost_per_order)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Efficiency</p>
              <p className="text-2xl font-bold text-green-600">{statistics.route_efficiency}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Shipping Routes</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">On-Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shipments</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {routes.map((route) => (
                  <tr key={route.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{route.name}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${route.type === 'Air' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>{route.type}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{route.avg_transit_days}d</td>
                    <td className="px-6 py-4 text-gray-500">{formatCurrency(route.cost_per_shipment)}</td>
                    <td className="px-6 py-4 text-green-600 font-bold">{route.on_time_rate}%</td>
                    <td className="px-6 py-4 text-blue-600">{formatNumber(route.shipments_mtd)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${route.optimization_score >= 85 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{route.optimization_score}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Carrier Performance</h2>
          </div>
          <div className="p-6 space-y-4">
            {carriers.map((carrier) => (
              <div key={carrier.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-gray-900">{carrier.name}</span>
                    <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{carrier.coverage}</span>
                  </div>
                  <span className="text-lg font-bold text-green-600">{carrier.performance_score}</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Cost Efficiency</p>
                    <p className="font-medium text-blue-600">{carrier.cost_efficiency}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Shipments</p>
                    <p className="font-medium text-purple-600">{formatNumber(carrier.shipments_mtd)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Avg Cost</p>
                    <p className="font-medium text-orange-600">{formatCurrency(carrier.avg_cost)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Performance</p>
                    <p className="font-medium text-green-600">{carrier.performance_score}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
